import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { updateDriver } from '../../shared/services/userService';
import { maxDeliveryRadiusMiles } from '../work/geo';

// iOS EditDriverProfileView delivery type options (exact list).
export const VEHICLE_TYPES = ['Car', 'Scooter', 'Bicycle', 'Walking'];

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-gray-100 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#FF6B35]/40 uppercase';

function formatMiles(m) {
  return m % 1 === 0 ? `${m}` : m.toFixed(1);
}

// iOS deliveryRangeExplanation — how the range + first-claim rules work.
function rangeExplanation(vehicleType) {
  switch ((vehicleType || '').toLowerCase()) {
    case 'walking':
      return "You'll only be offered deliveries within half a mile of the restaurant. Walkers get first claim on nearby orders — motorized drivers see them only when no walker is active.";
    case 'bicycle':
      return "You'll only be offered deliveries within 1 mile of the restaurant. Cyclists get first claim on short orders — motorized drivers see them only when no cyclist is active.";
    default:
      return '';
  }
}

// Writes drivers/{uid}: vehicleType, vehicleNumber, licenseNumber —
// same fields as iOS AuthManager.updateDriverProfile. Walking and bicycle
// couriers have no plate/license, so those are cleared on save.
export default function VehicleSheet({ open, onClose, driverId, driver }) {
  const [vehicleType, setVehicleType] = useState('Car');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setVehicleType(driver?.vehicleType || 'Car');
      setVehicleNumber(driver?.vehicleNumber || '');
      setLicenseNumber(driver?.licenseNumber || '');
      setError('');
      setSaving(false);
    }
  }, [open, driver]);

  const radius = maxDeliveryRadiusMiles(vehicleType);
  const smallVehicle = radius != null;

  async function save() {
    if (!driverId || saving) return;
    setSaving(true);
    setError('');
    try {
      await updateDriver(driverId, {
        vehicleType,
        // Walkers and cyclists have no plate/license — clear stale values
        vehicleNumber: smallVehicle ? '' : vehicleNumber.trim(),
        licenseNumber: smallVehicle ? '' : licenseNumber.trim(),
      });
      onClose();
    } catch (e) {
      console.error('updateDriver vehicle', e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Delivery Information">
      <div className="px-4 pb-6 space-y-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            Delivery Type
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {VEHICLE_TYPES.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setVehicleType(t)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-800 active:bg-gray-50"
              >
                {t}
                {vehicleType === t && <Check size={17} className="text-[#FF6B35]" />}
              </button>
            ))}
          </div>
        </div>

        {smallVehicle ? (
          <div className="bg-white rounded-2xl border border-gray-100 px-4 py-3 space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Delivery Range</span>
              <span className="font-medium text-gray-800">{formatMiles(radius)} mi</span>
            </div>
            <p className="text-xs text-gray-400">{rangeExplanation(vehicleType)}</p>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                Vehicle Number
              </span>
              <input
                className={inputCls}
                value={vehicleNumber}
                onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
                placeholder="e.g. P 1234"
                autoCapitalize="characters"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                License Number
              </span>
              <input
                className={inputCls}
                value={licenseNumber}
                onChange={e => setLicenseNumber(e.target.value.toUpperCase())}
                placeholder="Driver's license number"
                autoCapitalize="characters"
              />
            </label>
          </>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
        <PrimaryButton onClick={save} disabled={saving}>
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Saving…
            </span>
          ) : (
            'Save'
          )}
        </PrimaryButton>
      </div>
    </Sheet>
  );
}
